/**
import { getVariantUrl } from '../../photos/AdaptivePhoto';
 * SxSFeedTemplate - Side-by-Side feed template
 *
 * Displays photo with critique comments in an independently scrollable side panel.
 * Desktop: 60/40 split (image left, critiques right with independent scroll).
 * Mobile: Vertical stack (image top, critiques bottom).
 *
 * @module PhotoItem2/templates/SxSFeedTemplate
 */

import { cn } from '@/lib/utils';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { CritiqueCard } from './CritiqueCard';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { SelectionIndicator } from '@/components/atoms';

/**
 * SxSFeedTemplate component
 *
 * Renders photo with critique comments in side-by-side layout (desktop) or stacked layout (mobile).
 * Critique panel scrolls independently on desktop, keeping image visible while reading feedback.
 * Uses AdaptivePhoto for automatic resolution selection based on container size.
 */
export function SxSFeedTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick,
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead

  // Sort critiques newest first
  const sortedCritiques = data.critiques
    ? [...data.critiques].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
    : [];

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to div
  return (
    <div className={cn('flex p-2 flex-col md:flex-row w-full bg-surface-1 rounded-lg overflow-hidden', className)} {...restProps}>
      {/* Image section - Left/Top (60% on desktop) */}
      <div
        onClick={onClick}
        className={cn(
          'relative w-full md:w-3/5 flex-shrink-0 bg-surface-2',
          selectable && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
          isSelected && 'ring-4 ring-primary'
        )}
      >
        <AdaptivePhoto
          photo={data.photoData}
          alt={data.photoData.description || ''}
          className="w-full h-auto"
          preferStorageUrl={true}
        />

        {/* Selection indicator */}
        <SelectionIndicator visible={isSelected} size="md" />
      </div>

      {/* Critique panel - Right/Bottom (40% on desktop, independently scrollable) */}
      <aside
        className="w-full md:w-2/5 bg-surface-1 overflow-y-auto max-h-screen md:max-h-[100vh] p-4 space-y-3"
        aria-label="Critique comments"
      >
        {sortedCritiques.length > 0 ? (
          sortedCritiques.map((critique) => (
            <CritiqueCard
              key={critique.id}
              critique={critique}
              photo={data.photoData}
              expanded={true}
              onCritiqueClick={onCritiqueClick}
            />
          ))
        ) : (
          <div className="text-muted text-center py-8 flex flex-col items-center justify-center min-h-[200px]">
            <p className="text-sm" aria-label="No comments available">
              No comments yet
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}
